/**
 * WrappedMapComponent.js
 * 
 * This component renders an interactive Leaflet map with clustering support for geoJSON features,
 * two selectable base layers (CartoDB Positron tile layer and Hexagon WMS imagery),
 * and optional object markers displayed when zoomed in and a feature is selected.
 * It uses React Leaflet and react-leaflet-cluster for marker clustering,
 * and provides callbacks to parent components for setting the selected feature.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MapContainer,
  TileLayer,
  LayersControl,
  WMSTileLayer,
  Marker,
  Popup,
  useMap
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

import MarkerClusterGroup from 'react-leaflet-cluster';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import pinImg from "./../../assets/images/pin.png";
import pinSelectedImg from "./../../assets/images/pin_selected.png";
import stopImg from './../../assets/images/traffic-elements/stop.png';
import trafficLightImg from './../../assets/images/traffic-elements/traffic-signal-ahead.png';
import yieldImg from './../../assets/images/traffic-elements/yield.png';
import speedLimitImg from "./../../assets/images/traffic-elements/speed-limit.png";
import bikeRoutImg from './../../assets/images/traffic-elements/bike-route.png'
import laneMergeImg from './../../assets/images/traffic-elements/lane-merge.png'

// Remove Leaflet's default icon lookup to allow custom icons
delete L.Icon.Default.prototype._getIconUrl;

/**
 * Helper to create a Leaflet Icon with given image URL
 * @param {string} iconUrl - Path to the image for the marker icon
 */
const createCustomIcon = (iconUrl) => new L.Icon({
  iconUrl: iconUrl,
  iconSize: [40, 40],
  iconAnchor: [12, 25],
  popupAnchor: [0, -20],
  shadowUrl,
  shadowSize: [26, 26]
});

// Pre-create icons for default markers and specific traffic elements
const defaultIcon = createCustomIcon(pinImg);
const selectedIcon = createCustomIcon(pinSelectedImg);
const stopIcon = createCustomIcon(stopImg);
const trafficLightIcon = createCustomIcon(trafficLightImg);
const yieldIcon = createCustomIcon(yieldImg);
const speedLimitIcon = createCustomIcon(speedLimitImg);
const bikeRouteIcon = createCustomIcon(bikeRoutImg);
const laneMergeIcon = createCustomIcon(laneMergeImg);

/**
 * Returns the appropriate icon based on the object's label
 * @param {string} label - The label of the traffic object
 */
const getObjectIcon = (label) => {
  switch (label && label.toLowerCase()) {
    case 'stop sign':
      return stopIcon;
    case 'traffic light':
      return trafficLightIcon;
    case 'yield sign':
      return yieldIcon;
    case 'speed limit':
      return speedLimitIcon;
    case 'bike route':
      return bikeRouteIcon;
    case 'lane merge':
      return laneMergeIcon;
    default:
      return defaultIcon;
  }
};
/**
 * Core map component that handles rendering markers, clustering, and object overlays
 * Props:
 * - setSelectedFeature: callback when a marker is clicked
 * - goToMarker: index of marker to focus programmatically
 * - geoJsonData: GeoJSON object with features to display
 * - selectedFeatureIndex: index of selected feature (not currently used)
 * - showBoxes: boolean to toggle display of object markers when zoomed in
 */
const MapComponent = ({ setSelectedFeature, goToMarker, geoJsonData, selectedFeatureIndex, showBoxes }) => {
  const map = useMap();
  const [markersData, setMarkersData] = useState([]);
  const [additionalMarkers, setAdditionalMarkers] = useState([]);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(map.getZoom());

  // Sync markersData with incoming GeoJSON features
  useEffect(() => {
    if (geoJsonData && geoJsonData.features) {
      setMarkersData(geoJsonData.features.map(feature => ({ ...feature, id: feature.id })));
    } else {
      setMarkersData([]);
    }
  }, [geoJsonData]);

  // Center map and highlight marker when goToMarker index changes
  useEffect(() => {
    if (goToMarker !== null && markersData[goToMarker]) {
      const selected = markersData[goToMarker];
      const { geometry: { coordinates } } = selected;
      map.setView([coordinates[1], coordinates[0]], 20);
      setSelectedFeature(selected);
      setSelectedMarkerId(selected.id);
    }
  }, [goToMarker, markersData, map, setSelectedFeature]);

  // Update currentZoom state on map zoom end
  useEffect(() => {
    const handleZoomEnd = () => {
      setCurrentZoom(map.getZoom());
    };
    map.on('zoomend', handleZoomEnd);
    return () => {
      map.off('zoomend', handleZoomEnd);
    };
  }, [map]);

  // When a marker is selected and zoom is high enough, show object markers (e.g., traffic signs)
  useEffect(() => {
    if (selectedMarkerId && showBoxes && currentZoom >= 18) {
      const selectedFeature = markersData.find(marker => marker.id === selectedMarkerId);
      if (selectedFeature && selectedFeature.properties && Array.isArray(selectedFeature.properties.objects)) {
        const objectMarkers = selectedFeature.properties.objects.map((object, index) => {
          if (object.location && typeof object.location.lon === 'number' && typeof object.location.lat === 'number') {
            return {
              id: `<span class="math-inline">\{selectedFeature\.id\}\-object\-</span>{index}`,
              geometry: {
                coordinates: [object.location.lon, object.location.lat],
              },
              properties: {
                label: object.label,
                category: object.category,
                confidence: object.confidence,
              },
            };
          }
          return null;
        }).filter(marker => marker !== null);
        setAdditionalMarkers(objectMarkers);
      } else {
        setAdditionalMarkers([]);
      }
    } else {
      setAdditionalMarkers([]);
    }
  }, [selectedMarkerId, markersData, showBoxes, currentZoom]);

  // URLs for base layers
  const cartoDBPositronUrl =
    'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
  const hexagonWMSUrl =
    'https://waprovisoimg.centralindia.cloudapp.azure.com/streaming/wms';

  return (
    <LayersControl position="topright">
      <LayersControl.BaseLayer checked name="CartoDB Positron">
        <TileLayer
          attribution="&copy; OpenStreetMap contributors &copy; CARTO"
          url={cartoDBPositronUrl}
          maxZoom={22}
        />
      </LayersControl.BaseLayer>
      <LayersControl.BaseLayer name="Hexagon Imagery">
        <WMSTileLayer
          url={hexagonWMSUrl}
          layers="HxGN_Imagery"
          format="image/png"
          transparent
          attribution="Hexagon WMS"
          maxZoom={22}
        />
      </LayersControl.BaseLayer>
      {/* Marker clustering for main features */}
      <MarkerClusterGroup chunkedLoading>
        {markersData.map((feature) => {
          const isSelected = feature.id === selectedMarkerId;
          return (
            <Marker
              icon={isSelected ? selectedIcon : defaultIcon}
              key={feature.id}
              position={[
                feature.geometry.coordinates[1],
                feature.geometry.coordinates[0],
              ]}
              eventHandlers={{
                click: () => {
                  setSelectedFeature(feature);
                  setSelectedMarkerId(feature.id);
                },
              }}
            />
          );
        })}
      </MarkerClusterGroup>
      {/* Optional object markers when zoomed in */}  
      {showBoxes && additionalMarkers.map((marker) => (
        <Marker
          icon={getObjectIcon(marker.properties.label)}
          key={marker.id}
          position={[
            marker.geometry.coordinates[1],
            marker.geometry.coordinates[0],
          ]}
        >
          <Popup>
            {marker.properties.label} ({marker.properties.category})
          </Popup>
        </Marker>
      ))}
    </LayersControl>
  );
};
/**
 * Wrapper component that provides the MapContainer and default center/zoom
 */
const WrappedMapComponent = (props) => (
  <MapContainer 
    center={[48.28762, -122.65778]}
    zoom={19}
    style={{ height: '100%', width: '100%' }}
  >
    <MapComponent {...props} />
  </MapContainer>
);

export default WrappedMapComponent;